import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Linkedin, Youtube, Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import { NAV_CATEGORIES, SOCIAL_LINKS } from '../mock';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageToggle from './LanguageToggle';
import BrandLogo from './BrandLogo';

const SOCIALS = [
  { Icon: Facebook,  href: SOCIAL_LINKS.facebook,  label: 'Facebook' },
  { Icon: Instagram, href: SOCIAL_LINKS.instagram, label: 'Instagram' },
  { Icon: Twitter,   href: SOCIAL_LINKS.twitter,   label: 'Twitter / X' },
  { Icon: Linkedin,  href: SOCIAL_LINKS.linkedin,  label: 'LinkedIn' },
  { Icon: Youtube,   href: SOCIAL_LINKS.youtube,   label: 'YouTube' },
  { Icon: MapPin,    href: SOCIAL_LINKS.google,    label: 'Google Business' },
];

const Footer = () => {
  const { t } = useLanguage();
  const catLabel = (cat) => t(`category.${cat.category}`, cat.label);

  return (
    <footer className="bg-[#0f2540] text-gray-300">
      <div className="max-w-[1500px] mx-auto px-4 md:px-8 xl:px-10 2xl:px-14 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 xl:gap-12">
        <div className="lg:col-span-4">
          <div className="mb-4">
            <BrandLogo variant="footer" showTagline />
          </div>
          <p className="text-sm leading-relaxed text-gray-400 mb-5 max-w-[440px]">
            {t('footer.blurb', 'Trusted income tax filing, GST registration, company incorporation and accounting services across India. Expert CAs, transparent pricing, on-time delivery.')}
          </p>
          <div className="flex flex-wrap gap-3">
            {SOCIALS.map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                title={label}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#14A098] flex items-center justify-center transition-colors"
              >
                <Icon className="w-4 h-4 text-white" />
              </a>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">{t('footer.company', 'Company')}</h4>
          <ul className="space-y-3 text-sm">
            {[
              ['/', t('footer.home', 'Home')],
              ['/about', t('footer.about_us', 'About Us')],
              ['/contact', t('footer.contact', 'Contact')],
              ['/referral', t('footer.referral', 'Referral')],
            ].map(([to, label]) => (
              <li key={to}><Link to={to} className="hover:text-[#14A098] transition-colors">{label}</Link></li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">{t('footer.top_services', 'Services')}</h4>
          <ul className="space-y-3 text-sm">
            {NAV_CATEGORIES.map((cat) => (
              <li key={cat.category}>
                <Link to={`/all-services?cat=${encodeURIComponent(cat.category)}`} className="hover:text-[#14A098] transition-colors">{catLabel(cat)}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-1">
          <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">{t('footer.legal', 'Legal')}</h4>
          <ul className="space-y-3 text-sm">
            {[
              ['/terms', t('footer.terms', 'Terms of Service')],
              ['/privacy', t('footer.privacy', 'Privacy Policy')],
            ].map(([to, label]) => (
              <li key={to}><Link to={to} className="hover:text-[#14A098] transition-colors whitespace-nowrap">{label}</Link></li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider">{t('footer.support', 'Support')}</h4>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-gray-400 whitespace-nowrap">{t('footer.language', 'Language')}</span>
              <LanguageToggle variant="dark" />
            </div>
          </div>
          <ul className="space-y-4 text-sm">
            <li className="flex gap-3">
              <MapPin className="w-4 h-4 text-[#14A098] mt-1 shrink-0" />
              <span>
                1st Floor, MIG 88, Rd Number 1, above Dr Kiran&apos;s Eye Hospital, Kukatpally Housing Board Colony, K P H B Phase 1, Kukatpally, Hyderabad, Telangana - 500072
                <br />
                <a
                  href="https://maps.app.goo.gl/sAdEzkpxoVAZzTnU7"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#14A098] underline"
                >
                  {t('footer.view_maps', 'View on Google Maps')}
                </a>
              </span>
            </li>
            <li className="flex gap-3 items-center">
              <Mail className="w-4 h-4 text-[#14A098] shrink-0" />
              <a href="mailto:contact@solvetax.in" className="hover:text-[#14A098]">contact@solvetax.in</a>
            </li>
            <li className="flex gap-3 items-center">
              <Mail className="w-4 h-4 text-[#14A098] shrink-0" />
              <a href="mailto:support@solvetax.in" className="hover:text-[#14A098]">support@solvetax.in</a>
            </li>
            <li className="flex gap-3 items-center">
              <Phone className="w-4 h-4 text-[#14A098] shrink-0" />
              <a href="tel:+919346004430" className="hover:text-[#14A098]">+91 93460 04430</a>
            </li>
            <li className="flex gap-3 items-center">
              <MessageCircle className="w-4 h-4 text-[#14A098] shrink-0" />
              <a href="https://wa.me/919346004430" target="_blank" rel="noopener noreferrer" className="hover:text-[#14A098]">{t('footer.whatsapp_chat', 'WhatsApp Chat')}</a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1500px] mx-auto px-4 md:px-8 xl:px-10 2xl:px-14 py-6 flex flex-col md:flex-row justify-between items-center text-xs text-gray-400 gap-2">
          <span>{t('footer.copyright', 'SOLVETAX © 2025. All rights reserved.')}</span>
          <span>{t('footer.tagline', 'Crafted with care for Indian taxpayers • GSTIN-ready • ICAI-aligned advisors')}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
